import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, ShoppingBag, Download, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SmartImage from '../components/SmartImage';
import ShareButton from '../components/ShareButton';
import api, { mediaUrl } from '../lib/api';
import { sanitizeHtml } from '../lib/sanitize';
import { useCart } from '../context/CartContext';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/shop/products/${slug}`).then((res) => setP(res.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="App"><Header /><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-mahogany" /></div><Footer /></div>;
  if (notFound || !p) return (
    <div className="App"><Header /><div className="text-center py-32 bg-cream"><p className="text-mahogany/70">Product not found.</p><Link to="/shop" className="pill-btn mt-4 inline-flex">Back to Shop</Link></div><Footer /></div>
  );

  const add = () => { addItem(p, 1); setAdded(true); setTimeout(() => setAdded(false), 1600); };
  const buyNow = () => { addItem(p, 1); navigate('/cart'); };

  return (
    <div className="App" data-testid="page-product-detail">
      <Header />
      <main className="bg-cream">
        <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12">
          <Link to="/shop" className="inline-flex items-center gap-2 text-mahogany/70 hover:text-mahogany mb-6" data-testid="product-back"><ArrowLeft className="w-4 h-4" /> Back to Shop</Link>
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <SmartImage src={mediaUrl(p.image)} alt={p.title} className="rounded-3xl aspect-[3/4]" />
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[11px] uppercase tracking-widest px-3 py-1 rounded-full bg-rose-soft text-mahogany">{p.category}</span>
                {p.product_type === 'digital' && <span className="text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-mahogany text-cream flex items-center gap-1"><Download className="w-3 h-3" /> Digital download</span>}
              </div>
              <h1 className="font-serif-display text-4xl md:text-5xl text-burgundy" data-testid="product-title">{p.title}</h1>
              <div className="mt-3 text-2xl text-mahogany font-medium">${p.price.toFixed(2)}</div>
              {p.short_description && <p className="mt-4 text-burgundy/85">{p.short_description}</p>}
              {p.description && <div className="prose-osho mt-5 text-burgundy/85 leading-relaxed" dangerouslySetInnerHTML={{ __html: sanitizeHtml(p.description) }} />}
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={add} className="pill-btn-outline" data-testid="product-add-cart">{added ? <>Added <Check className="w-4 h-4" /></> : <>Add to Cart <ShoppingBag className="w-4 h-4" /></>}</button>
                <button onClick={buyNow} className="pill-btn" data-testid="product-buy-now">Buy Now</button>
                <ShareButton title={p.title} text={`Check out ${p.title} at the Osho Universal Eco-Store`} testid="share-product" />
              </div>
              {p.product_type === 'digital' && <p className="mt-4 text-xs text-mahogany/60">Your download link will be available on the confirmation page immediately after payment.</p>}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ProductDetail;
